/* AccountShell — `/${tenantCode}/account/*` content frame.
 *
 * Top chrome (← · Org · Cart · Settings · Profile; prefs on Settings sheet) and
 * bottom nav live in CustomerAppShell.
 * Content-row hierarchical ← is a fallback when the fixed header does not own back;
 * under shell depth chrome it self-suppresses via ShellDepthBackProvider.
 * Embedded `/account/donate*`: fixed shell owns ← when HierarchicalBackFlow publishes
 * (DonationStepLayout / CustomerSubpageBackBar suppress); AccountShell skips its auto-bar
 * so content does not double-chevron. No dual ←.
 */

import { Outlet, useLocation } from 'react-router-dom';
import {
  CUSTOMER_CONTENT_FULL_CLASS,
  CUSTOMER_CONTENT_STANDARD_CLASS,
  CUSTOMER_TAB_CONTENT_CLASS,
} from '../../app/customerTopBarStyles.js';
import { ErrorIsolationProbe } from '../../test/e2e/errorIsolationProbe.js';
import { CustomerSubpageBackBar } from '../../shared/ui/CustomerSubpageBackBar.js';
import { isAccountEmbeddedDonationPath } from '../donation/donationRoutePaths.js';

export { AccountThemeToggle } from '../../app/AccountThemeToggle.js';

/**
 * Account-relative segments after the hub base.
 * Platform: `/account/*` → rest. Tenant legacy: `/:tenantCode/account/*` → rest.
 */
export function accountHubRestSegments(pathname: string): readonly string[] | null {
  const pathOnly = pathname.split(/[?#]/, 1)[0] ?? pathname;
  const segments = pathOnly.split('/').filter((part) => part.length > 0);
  if (segments[0] === 'account') {
    return segments.slice(1);
  }
  if (segments.length >= 2 && segments[1] === 'account') {
    return segments.slice(2);
  }
  return null;
}

/** True when rest is exact orders list hub (`orders` only). */
export function isAccountOrdersListHubPath(pathname: string): boolean {
  const rest = accountHubRestSegments(pathname);
  return rest !== null && rest.length === 1 && rest[0] === 'orders';
}

/**
 * True when rest is order detail (`orders/:transactionId`) — any non-empty segment,
 * not only numeric ids (G13).
 */
export function isAccountOrderDetailPath(pathname: string): boolean {
  const rest = accountHubRestSegments(pathname);
  return (
    rest !== null &&
    rest.length === 2 &&
    rest[0] === 'orders' &&
    (rest[1] ?? '').trim().length > 0
  );
}

/**
 * G5 — POP from order detail onto the orders list hub after overlay dismiss:
 * accept history landing on the list (do not restoreLeft / reopen detail).
 */
export function isAccountOrderDetailToOrdersListPop(
  leftPath: string,
  nextPath: string,
): boolean {
  return isAccountOrderDetailPath(leftPath) && isAccountOrdersListHubPath(nextPath);
}

/**
 * FULL-width list hubs only (G10/G13). Do not use `pathname.includes('/account/orders')` —
 * that also matches order detail `/account/orders/:transactionId`.
 *
 * True: exact orders list (`/account/orders`, trailing slash OK), order detail overlay
 * route (`/account/orders/:transactionId` — any segment), and shop directory plus
 * descendants (`/account/shop`, `/account/shop/sales-points`, …).
 * Same rules under `/:tenantCode/account/…`.
 */
export function isAccountDirectoryFullWidthPath(pathname: string): boolean {
  const rest = accountHubRestSegments(pathname);
  if (rest === null || rest.length === 0) {
    return false;
  }
  if (rest[0] === 'orders') {
    if (rest.length === 1) {
      return true;
    }
    // Order detail modal route — keep list hub full-width underneath (any transactionId).
    return isAccountOrderDetailPath(pathname);
  }
  return rest[0] === 'shop';
}

export function AccountShell(): JSX.Element {
  const location = useLocation();
  const embeddedDonateOwnsBack = isAccountEmbeddedDonationPath(location.pathname);
  const useDirectoryWidth = isAccountDirectoryFullWidthPath(location.pathname);
  const contentWidthClass = useDirectoryWidth
    ? CUSTOMER_CONTENT_FULL_CLASS
    : CUSTOMER_CONTENT_STANDARD_CLASS;

  return (
    <div className={`${CUSTOMER_TAB_CONTENT_CLASS} ${contentWidthClass}`}>
      <ErrorIsolationProbe feature="account" />
      {embeddedDonateOwnsBack ? null : (
        <CustomerSubpageBackBar testId="customer-account-hierarchical-back" />
      )}
      <Outlet />
    </div>
  );
}
